import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatNumber, titleCase } from '../../lib/format'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Skeleton,
} from '../../components/ui'
import { CoinIcon } from '../../components/icons'

/* Platform-wide community-benefit levy ledger - super_admin only (see
 * GET /api/v1/admin/community-levy). Not visible to team_leader/company
 * even though they hold reports.view.sensitive for their own org's
 * Finance page - this is the one figure the terms describe as admin-only,
 * because it aggregates every cooperative's totals in one place. Always
 * derived live from non-cancelled loads, so it can't drift out of sync. */

interface LevyBreakdown {
  load_count: number
  cooperative_share_zmw: number
  royalty_zmw: number
  community_share_zmw: number
  farmer_share_zmw: number
  total_zmw: number
}
interface OrgLevyRow extends LevyBreakdown {
  org_id: string
  org_name: string
  org_type: string
}
interface LevyResponse {
  ok: boolean
  rates: {
    cooperative_share_per_load_zmw: number
    royalty_per_truckload_zmw: number
    community_share_per_load_zmw: number
    farmer_share_per_load_zmw: number
    total_per_load_zmw: number
  }
  platform: LevyBreakdown
  by_cooperative: OrgLevyRow[]
}

export default function CommunityLevy() {
  // Summary = platform-wide totals; Table = the per-organisation ledger.
  // Same Summary|Table split as every other reporting page.
  const [view, setView] = useState<'summary' | 'table'>('summary')
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'community-levy'],
    queryFn: () => api.get<LevyResponse>('/admin/community-levy'),
  })

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Community-benefit levy</h1>
          <p className="page-head__sub">
            Platform-wide totals across every load ever dispatched — cooperative, royalty, community and
            farmer shares, per the published terms.
          </p>
        </div>
      </div>

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading ? (
        <div className="stack"><Skeleton h={90} /><Skeleton h={200} /></div>
      ) : !data ? null : (
        <>
          <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
            <Button variant={view === 'summary' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('summary')}>
              Summary
            </Button>
            <Button variant={view === 'table' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('table')}>
              Table
            </Button>
          </div>

          {view === 'summary' && (
          <div className="grid grid--stats" style={{ marginBottom: 'var(--sp-6)' }}>
            <Stat label="Loads counted" value={formatNumber(data.platform.load_count, 0)} />
            <Stat label={`Cooperative (K${data.rates.cooperative_share_per_load_zmw}/load)`}
                  value={`K${formatNumber(data.platform.cooperative_share_zmw, 0)}`} />
            <Stat label={`Royalty (K${data.rates.royalty_per_truckload_zmw}/load)`}
                  value={`K${formatNumber(data.platform.royalty_zmw, 0)}`} />
            <Stat label={`Community (K${data.rates.community_share_per_load_zmw}/load)`}
                  value={`K${formatNumber(data.platform.community_share_zmw, 0)}`} />
            <Stat label={`Farmer (K${data.rates.farmer_share_per_load_zmw}/load)`}
                  value={`K${formatNumber(data.platform.farmer_share_zmw, 0)}`} />
            <Stat label="Total accumulated" value={`K${formatNumber(data.platform.total_zmw, 0)}`} accent />
          </div>
          )}

          {view === 'table' && (
          <Card>
            <CardHeader title="By cooperative / company" sub="Sorted by total accumulated levy" />
            <CardBody flush>
              {data.by_cooperative.length === 0 ? (
                <EmptyState icon={<CoinIcon size={22} />} title="No loads yet"
                            body="Once organisations start dispatching loads, their levy totals will appear here." />
              ) : (
                <>
                  {/* PWA tap rows - the 8-column ledger can't fit a phone;
                      each org gets a row with its total at right and the
                      share breakdown underneath. Desktop keeps the table. */}
                  <div className="mobile-only">
                    {data.by_cooperative.map((r) => (
                      <div key={r.org_id} className="lrow">
                        <div className="lrow__main">
                          <div className="lrow__title">{r.org_name}</div>
                          <div className="lrow__sub">
                            {r.load_count} load{r.load_count === 1 ? '' : 's'} · Coop K{formatNumber(r.cooperative_share_zmw, 0)} ·
                            Roy K{formatNumber(r.royalty_zmw, 0)} · Comm K{formatNumber(r.community_share_zmw, 0)} ·
                            Farm K{formatNumber(r.farmer_share_zmw, 0)}
                          </div>
                        </div>
                        <div className="lrow__end" style={{ fontWeight: 700, color: 'var(--accent-text)' }}>
                          K{formatNumber(r.total_zmw, 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                <div className="table-wrap desktop-only">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Organisation</th><th>Type</th>
                        <th className="table__num">Loads</th>
                        <th className="table__num">Cooperative</th>
                        <th className="table__num">Royalty</th>
                        <th className="table__num">Community</th>
                        <th className="table__num">Farmer</th>
                        <th className="table__num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_cooperative.map((r) => (
                        <tr key={r.org_id}>
                          <td style={{ fontWeight: 600 }}>{r.org_name}</td>
                          <td>{r.org_type ? titleCase(r.org_type) : '—'}</td>
                          <td className="table__num">{r.load_count}</td>
                          <td className="table__num">K{formatNumber(r.cooperative_share_zmw, 0)}</td>
                          <td className="table__num">K{formatNumber(r.royalty_zmw, 0)}</td>
                          <td className="table__num">K{formatNumber(r.community_share_zmw, 0)}</td>
                          <td className="table__num">K{formatNumber(r.farmer_share_zmw, 0)}</td>
                          <td className="table__num" style={{ fontWeight: 700, color: 'var(--accent-text)' }}>
                            K{formatNumber(r.total_zmw, 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </CardBody>
          </Card>
          )}
        </>
      )}
    </>
  )
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <Card hover>
      <div className="stat">
        <div className="stat__top">
          <span className="stat__label">{label}</span>
        </div>
        <div className="stat__value" style={accent ? { color: 'var(--accent-text)' } : undefined}>{value}</div>
      </div>
    </Card>
  )
}
